// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CoreState } from '@app/core';
import { RolesState, rolesReducer } from './roles.reducer';
import { UsersState, usersReducer } from './users.reducer';
import { PoliciesState, policiesReducer } from './policies.reducer';
import {
  PolicyStatementState,
  policyStatementReducer,
  getStatementResources,
  getStatementActions,
  getStatementServices,
  getResourceActionsNeedLoading,
  getLoadableResourceActions
} from './policy-statement.reducer';

import {
  PolicyFormState,
  policyFormReducer,
  getPolicyFormDetails,
  getStatementSelections,
  getPolicyDraft
} from './policy-form.reducer';
import {
  RoleDetailsState,
  roleDetailsReducer,
  getRoleDetails,
  getSelectedUsers,
  getSelectedRoleScopeCategory,
  getSelectedRoleScopeConstraints,
  getRoleUserSelections,
  getUserToEdit,
  getRoleDetailsLoadable,
  getRoleGroupSelections,
  getSelectedRoleScopeCategoryForGroup
} from './role-details.reducer';
import { scopesReducer, ScopesState, getScopeCategories, getScopeConstraints } from './role-scope.reducer';
import { ScopesEffects } from './role-scope.effects';
import { RolesEffects } from './roles.effects';
import { UsersEffects } from './users.effects';
import { RoleDetailsEffects } from './role-details.effects';
import { PoliciesEffects } from './policies.effects';
import { PolicyStatementEffects } from './policy-statement.effects';
import { groupsReducer, GroupsState } from './groups/groups.reducer';
import { GroupsEffects } from './groups/groups.effects';
import { GroupDetailsState, groupDetailsReducer } from './group-details/group-details.reducers';
import { GroupDetailsEffects } from './group-details/group-details.effects';
import { PolicyDetailsState, policyDetailsReducer } from './policy-details/policy-details.reducer';
import { PolicyDetailsEffects } from './policy-details/policy-details.effects';
import { userDetailsReducer } from './user-details/user-details.reducer';
import { UserDetailsState } from './user-details/user-details.interfaces';
import { UserDetailsEffects } from './user-details/user-details.effects';
import { OrganizationsEffects } from './organizations.effects';
import { organizationsReducer, OrganizationsState } from './organizations.reducer';
import { organizationDetailsReducer } from './organization-details/organization-details.reducer';
import { OrganizationDetailsState } from './organization-details/organization-details.interfaces';
import { OrganizationDetailsEffects } from './organization-details/organization-details.effects';

export * from './roles.actions';
export * from './roles.reducer';
export * from './role-scope.actions';
export * from './role-scope.reducer';
export * from './users.actions';
export * from './users.reducer';
export * from './role-details.actions';
export * from './role-details.reducer';
export * from './policy-form.actions';
export * from './policy-form.reducer';
export * from './policies.actions';
export * from './policies.reducer';
export * from './policy-statement.actions';
export * from './policy-statement.reducer';
export * from './organizations.actions';
export * from './organizations.reducer';

export const reducers = {
  roles: rolesReducer,
  roleDetails: roleDetailsReducer,
  users: usersReducer,
  userDetails: userDetailsReducer,
  policies: policiesReducer,
  policyDetails: policyDetailsReducer,
  policyForm: policyFormReducer,
  policyStatement: policyStatementReducer,
  scopes: scopesReducer,
  groups: groupsReducer,
  groupDetails: groupDetailsReducer,
  organizations: organizationsReducer,
  organizationDetails: organizationDetailsReducer
} as any;

export interface State extends CoreState {
  roles: RolesState;
  roleDetails: RoleDetailsState;
  users: UsersState;
  userDetails: UserDetailsState;
  policies: PoliciesState;
  policyDetails: PolicyDetailsState;
  policyForm: PolicyFormState;
  policyStatement: PolicyStatementState;
  scopes: ScopesState;
  groups: GroupsState;
  groupDetails: GroupDetailsState;
  organizations: OrganizationsState;
  organizationDetails: OrganizationDetailsState;
}

export const IAMEffects = [
  RolesEffects,
  RoleDetailsEffects,
  UsersEffects,
  ScopesEffects,
  PoliciesEffects,
  PolicyStatementEffects,
  GroupsEffects,
  GroupDetailsEffects,
  PolicyDetailsEffects,
  UserDetailsEffects,
  OrganizationsEffects,
  OrganizationDetailsEffects
];

export const ACCESS_MANAGEMENT_FEATURE_NAME = 'iam';

export const accessManagementSelector = createFeatureSelector<State>(ACCESS_MANAGEMENT_FEATURE_NAME);

export const selectRoles = createSelector(accessManagementSelector, state => state.roles);

export const selectRoleDetails = createSelector(accessManagementSelector, state => state.roleDetails);

export const selectUsers = createSelector(accessManagementSelector, state => state.users);

export const selectScopes = createSelector(accessManagementSelector, state => state.scopes);

export const selectRoleDetailsLoadable = (roleId: string) =>
  createSelector(selectRoleDetails, getRoleDetailsLoadable(roleId));

export const selectRoleDetail = (roleId: string) => createSelector(selectRoleDetails, getRoleDetails(roleId));

export const selectPolicies = createSelector(accessManagementSelector, state => state.policies);

export const selectPolicyForm = createSelector(accessManagementSelector, state => state.policyForm);

export const selectPolicyDraft = createSelector(selectPolicyForm, getPolicyDraft);

export const selectPolicyFormDetails = createSelector(selectPolicyForm, getPolicyFormDetails);

export const selectPolicyStatement = createSelector(accessManagementSelector, state => state.policyStatement);

export const selectSelectedRoleScopeCategory = (roleId: string) =>
  createSelector(selectRoleDetails, getSelectedRoleScopeCategory(roleId));

export const selectSelectedRoleScopeCategoryForGroup = (roleId: string) =>
  createSelector(selectRoleDetails, getSelectedRoleScopeCategoryForGroup(roleId));

export const selectSelectedRoleScopeConstraints = (roleId: string) =>
  createSelector(selectRoleDetails, getSelectedRoleScopeConstraints(roleId));

export const selectSelectedUsers = (roleId: string) => createSelector(selectRoleDetails, getSelectedUsers(roleId));

export const selectScopeCategories = createSelector(selectScopes, getScopeCategories);

export const selectScopeConstraints = (categoryId: string) =>
  createSelector(selectScopes, getScopeConstraints(categoryId));

export const selectRoleUserSelections = (roleId: string) =>
  createSelector(selectRoleDetails, getRoleUserSelections(roleId));

export const selectRoleGroupSelections = (roleId: string) =>
  createSelector(selectRoleDetails, getRoleGroupSelections(roleId));

export const selectUserToEdit = (roleId: string) => createSelector(selectRoleDetails, getUserToEdit(roleId));

export const selectPolicyStatementSelections = createSelector(selectPolicyForm, getStatementSelections);

export const selectStatementServices = createSelector(selectPolicyStatement, getStatementServices);

export const selectStatementResources = (serviceId: string) =>
  createSelector(selectPolicyStatement, getStatementResources(serviceId));

export const selectStatementActions = (serviceId: string, resourceId: string) =>
  createSelector(selectPolicyStatement, getStatementActions(serviceId, resourceId));

export const selectResourceActionsNeedLoading = (serviceId: string) =>
  createSelector(selectPolicyStatement, getResourceActionsNeedLoading(serviceId));

export const selectLoadableResourceActions = (serviceId: string) =>
  createSelector(selectPolicyStatement, getLoadableResourceActions(serviceId));

export const selectOrganizations = createSelector(accessManagementSelector, state => state.organizations);



// WEBPACK FOOTER //
// ./src/app/access-management/state/index.ts